/**
 * CloudStoragePicker — modal for browsing & importing files from Google Drive.
 *
 * Props:
 *   open        – controls visibility
 *   onClose     – close modal
 *   onFileImported – callback with imported file info
 *   onFileSelected – callback with selected file metadata only (no server import)
 *   accept      – 'video' | 'image' | 'all' (default: 'all')
 *   mode        – 'import' | 'select' (default: 'import')
 */

import { useState, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { FriendlyErrorMapper } from "@/lib/errors/FriendlyErrorMapper";
import {
  X,
  FolderOpen,
  ChevronLeft,
  Cloud,
  Download,
  Loader2,
  Image,
  Film,
  HardDrive,
  CheckCircle2,
  Unplug,
  RefreshCw,
} from "lucide-react";

// ═══════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════

type CloudProvider = "google_drive";
type AcceptFilter = "video" | "image" | "all";

interface CloudFile {
  id: string;
  name: string;
  mimeType: string;
  size: number;
  isFolder: boolean;
  thumbnailUrl?: string;
  modifiedAt?: string;
}

export interface ImportedFile {
  fileId: string;
  originalName: string;
  mimeType: string;
  size: number;
  path: string;
}

export interface SelectedCloudFile {
  provider: CloudProvider;
  fileId: string;
  originalName: string;
  mimeType: string;
  size: number;
  modifiedAt?: string;
  thumbnailUrl?: string;
}

interface CloudStoragePickerProps {
  open: boolean;
  onClose: () => void;
  onFileImported?: (file: ImportedFile) => void;
  onFileSelected?: (file: SelectedCloudFile) => void;
  accept?: AcceptFilter;
  mode?: "import" | "select";
}

interface ProviderStatus {
  connected: boolean;
  email?: string;
  connectedAt?: string;
}

interface StatusResponse {
  google_drive: ProviderStatus;
}

// ═══════════════════════════════════════════════════════
// Provider config
// ═══════════════════════════════════════════════════════

interface ProviderConfig {
  label: string;
  icon: string;
  color: string;
  bgColor: string;
  borderColor: string;
}

const PROVIDER_CONFIG: Record<CloudProvider, ProviderConfig> = {
  google_drive: {
    label: "Google Drive",
    icon: "📁",
    color: "text-blue-400",
    bgColor: "bg-blue-500/10",
    borderColor: "border-blue-500/20",
  },
};

const ALL_PROVIDERS: CloudProvider[] = ["google_drive"];

// ═══════════════════════════════════════════════════════
// Helpers
// ═══════════════════════════════════════════════════════

function formatFileSize(bytes: number): string {
  if (bytes === 0) return "—";
  const units = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${units[i]}`;
}
function matchesFilter(mimeType: string, filter: AcceptFilter): boolean {
  if (filter === "all") return true;
  if (filter === "video") return mimeType.startsWith("video/");
  return mimeType.startsWith("image/");
}

function getFileIcon(file: CloudFile) {
  if (file.isFolder) return <FolderOpen className="w-5 h-5 text-amber-400" />;
  if (file.mimeType.startsWith("video/")) return <Film className="w-5 h-5 text-purple-400" />;
  return <Image className="w-5 h-5 text-emerald-400" />;
}

// ═══════════════════════════════════════════════════════
// Component
// ═══════════════════════════════════════════════════════

export default function CloudStoragePicker({
  open,
  onClose,
  onFileImported,
  onFileSelected,
  accept = "all",
  mode = "import",
}: CloudStoragePickerProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === "pl" ? "pl-PL" : "en-US";
  const queryClient = useQueryClient();
  const [activeProvider, setActiveProvider] = useState<CloudProvider | null>(null);
  const [folderStack, setFolderStack] = useState<Array<{ id: string; name: string }>>([]);
  const [importingFileId, setImportingFileId] = useState<string | null>(null);
  const [connectError, setConnectError] = useState<string | null>(null);

  const currentFolderId =
    folderStack.length > 0 ? folderStack[folderStack.length - 1].id : undefined;

  // Reset state on close
  useEffect(() => {
    if (!open) {
      setActiveProvider(null);
      setFolderStack([]);
      setImportingFileId(null);
      setConnectError(null);
    }
  }, [open]);

  // OAuth popup reports back via postMessage → refresh connection status.
  // Mounted with the component (not per-click, and NOT gated on `open`) so the listener
  // is always cleaned up, yet a callback landing after the user closed the modal still
  // invalidates the cached status — otherwise `staleTime` would serve a stale
  // "not connected" for up to 30s on the next open.
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // The callback page is served from the app's own origin: the OAuth state cookie is
      // host-only, so a callback on any other host could never pass the state check.
      // Without this guard any cross-origin frame/window could spoof the message.
      if (event.origin !== window.location.origin) return;
      if (event.data?.type !== "cloud_storage_callback") return;
      queryClient.invalidateQueries({ queryKey: ["cloud-storage-status"] });
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [queryClient]);

  // ─── Status query ───
  const statusQuery = useQuery<StatusResponse>({
    queryKey: ["cloud-storage-status"],
    queryFn: () => apiRequest("GET", "/api/integrations/cloud-storage/status"),
    enabled: open,
    staleTime: 30_000,
  });

  // ─── Files query ───
  const filesQuery = useQuery<{ files: CloudFile[] }>({
    queryKey: ["cloud-storage-files", activeProvider, currentFolderId],
    queryFn: () => {
      const params = currentFolderId ? `?folderId=${encodeURIComponent(currentFolderId)}` : "";
      return apiRequest("GET", `/api/integrations/cloud-storage/files/${activeProvider}${params}`);
    },
    enabled: !!activeProvider && !!statusQuery.data?.[activeProvider]?.connected,
    staleTime: 30_000,
  });

  // ─── Import mutation ───
  const importMutation = useMutation({
    mutationFn: (fileId: string) =>
      apiRequest<ImportedFile>("POST", `/api/integrations/cloud-storage/import/${activeProvider}`, {
        fileId,
      }),
    onSuccess: (data: ImportedFile) => {
      onFileImported?.(data);
      onClose();
    },
    onSettled: () => setImportingFileId(null),
  });

  // ─── Disconnect mutation ───
  const disconnectMutation = useMutation({
    mutationFn: (provider: CloudProvider) =>
      apiRequest("DELETE", `/api/integrations/cloud-storage/disconnect/${provider}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cloud-storage-status"] });
      setActiveProvider(null);
    },
  });

  // ─── Connect handler (popup) ───
  // The popup must be opened on about:blank and navigated via location.href.
  // Passing the URL straight to window.open() and then calling document.write()
  // aborts the pending navigation, leaving the popup stuck on the placeholder.
  const handleConnect = useCallback(
    async (provider: CloudProvider) => {
      setConnectError(null);

      const popup = window.open("about:blank", `${provider}_oauth`, "width=600,height=700");
      if (!popup) {
        setConnectError(t("cloudPicker.popupBlocked"));
        return;
      }

      // Best-effort splash. window.open() with an already-used window name returns the
      // existing popup without navigating it synchronously — on a second Connect click it
      // is still on accounts.google.com, so touching its document throws SecurityError.
      // The splash is cosmetic; never let it abort the flow.
      try {
        popup.document.write(
          `<html><body style="background:#0d0f14;color:white;font-family:sans-serif;display:flex;align-items:center;justify-content:center;height:100vh;margin:0"><p>${t("cloudPicker.redirecting")}</p></body></html>`
        );
        popup.document.close();
      } catch {
        /* cross-origin popup reuse — skip the placeholder, the redirect below still works */
      }

      try {
        const data = (await apiRequest(
          "GET",
          `/api/integrations/cloud-storage/connect/${provider}`
        )) as { url?: string };

        if (!data.url) throw new Error("Missing OAuth URL");
        if (!popup.closed) popup.location.href = data.url;
      } catch (err) {
        if (!popup.closed) popup.close();
        setConnectError(FriendlyErrorMapper.map(err).description);
      }
    },
    [t]
  );

  // ─── Navigation ───
  const navigateToFolder = useCallback((file: CloudFile) => {
    setFolderStack((prev) => [...prev, { id: file.id, name: file.name }]);
  }, []);

  const navigateBack = useCallback(() => {
    setFolderStack((prev) => prev.slice(0, -1));
  }, []);

  const handleImport = useCallback(
    (file: CloudFile) => {
      if (!activeProvider) return;

      if (mode === "select") {
        onFileSelected?.({
          provider: activeProvider,
          fileId: file.id,
          originalName: file.name,
          mimeType: file.mimeType,
          size: file.size,
          modifiedAt: file.modifiedAt,
          thumbnailUrl: file.thumbnailUrl,
        });
        onClose();
        return;
      }

      setImportingFileId(file.id);
      importMutation.mutate(file.id);
    },
    [activeProvider, importMutation, mode, onClose, onFileSelected]
  );

  if (!open) return null;

  const status = statusQuery.data;
  const files = filesQuery.data?.files ?? [];
  const filteredFiles = files.filter((f) => f.isFolder || matchesFilter(f.mimeType, accept));

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-[#0d0f14] border border-white/[0.12] rounded-2xl w-full max-w-2xl max-h-[80vh] flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/[0.08]">
          <div className="flex items-center gap-3">
            <Cloud className="w-5 h-5 text-white/60" />
            <h2 className="text-base font-semibold text-white">
              {activeProvider ? PROVIDER_CONFIG[activeProvider].label : t("cloudPicker.title")}
            </h2>
            {activeProvider && folderStack.length > 0 && (
              <span className="text-xs text-white/40 truncate max-w-[200px]">
                / {folderStack.map((f) => f.name).join(" / ")}
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-white/[0.06] text-white/40 hover:text-white/70 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {!activeProvider ? (
            <ProviderSelector
              status={status}
              isLoading={statusQuery.isLoading}
              onSelect={setActiveProvider}
              onConnect={handleConnect}
              onDisconnect={(p) => disconnectMutation.mutate(p)}
              connectError={connectError}
            />
          ) : (
            <FileBrowser
              files={filteredFiles}
              isLoading={filesQuery.isLoading}
              error={filesQuery.error}
              importingFileId={importingFileId}
              folderStack={folderStack}
              onNavigateFolder={navigateToFolder}
              onNavigateBack={navigateBack}
              onImport={handleImport}
              onRefresh={() => filesQuery.refetch()}
              importError={importMutation.error}
              actionLabel={mode === "select" ? t("cloudPicker.select") : t("cloudPicker.import")}
              locale={locale}
            />
          )}
        </div>

        {/* Footer */}
        {activeProvider && (
          <div className="px-6 py-3 border-t border-white/[0.08] flex items-center justify-between">
            <button
              onClick={() => {
                setActiveProvider(null);
                setFolderStack([]);
              }}
              className="flex items-center gap-2 text-sm text-white/50 hover:text-white/80 transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
              {t("cloudPicker.chooseOther")}
            </button>
            <span className="text-xs text-white/30">
              {accept === "video" && t("cloudPicker.acceptVideo")}
              {accept === "image" && t("cloudPicker.acceptImage")}
              {accept === "all" && t("cloudPicker.acceptAll")}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// Sub-components
// ═══════════════════════════════════════════════════════

function ProviderSelector({
  status,
  isLoading,
  onSelect,
  onConnect,
  onDisconnect,
  connectError,
}: {
  status: StatusResponse | undefined;
  isLoading: boolean;
  onSelect: (p: CloudProvider) => void;
  onConnect: (p: CloudProvider) => void;
  onDisconnect: (p: CloudProvider) => void;
  connectError: string | null;
}) {
  const { t } = useTranslation();
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 text-white/40 animate-spin" />
      </div>
    );
  }

  return (
    <>
      {connectError && (
        <div className="mb-4 px-4 py-2 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
          {t("cloudPicker.connectFailed", { message: connectError })}
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {ALL_PROVIDERS.map((provider) => {
          const config = PROVIDER_CONFIG[provider];
          const providerStatus = status?.[provider];
          const isConnected = providerStatus?.connected ?? false;

          return (
            <div
              key={provider}
              className={`rounded-xl border p-5 transition-all ${config.borderColor} ${config.bgColor}`}
            >
              <div className="flex items-center gap-3 mb-3">
                <span className="text-2xl">{config.icon}</span>
                <div>
                  <p className="text-sm font-semibold text-white">{config.label}</p>
                  {isConnected && providerStatus?.email && (
                    <p className="text-xs text-white/40 truncate max-w-[180px]">
                      {providerStatus.email}
                    </p>
                  )}
                </div>
              </div>

              {isConnected ? (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => onSelect(provider)}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-white/[0.08] hover:bg-white/[0.14] text-white transition-colors"
                  >
                    <HardDrive className="w-4 h-4" />
                    {t("cloudPicker.browseFiles")}
                  </button>
                  <button
                    onClick={() => onDisconnect(provider)}
                    className="p-2 rounded-lg hover:bg-red-500/20 text-white/30 hover:text-red-400 transition-colors"
                    title={t("cloudPicker.disconnect")}
                  >
                    <Unplug className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => onConnect(provider)}
                  className={`w-full flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-lg ${config.color} border ${config.borderColor} hover:bg-white/[0.06] transition-colors`}
                >
                  <CheckCircle2 className="w-4 h-4" />
                  {t("cloudPicker.connect", { name: config.label })}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}

function FileBrowser({
  files,
  isLoading,
  error,
  importingFileId,
  folderStack,
  onNavigateFolder,
  onNavigateBack,
  onImport,
  onRefresh,
  importError,
  actionLabel,
  locale,
}: {
  files: CloudFile[];
  isLoading: boolean;
  error: Error | null;
  importingFileId: string | null;
  folderStack: Array<{ id: string; name: string }>;
  onNavigateFolder: (file: CloudFile) => void;
  onNavigateBack: () => void;
  onImport: (file: CloudFile) => void;
  onRefresh: () => void;
  importError: Error | null;
  actionLabel: string;
  locale: string;
}) {
  const { t } = useTranslation();
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 text-white/40 animate-spin" />
        <span className="ml-3 text-white/40 text-sm">{t("cloudPicker.loadingFiles")}</span>
      </div>
    );
  }

  if (error) {
    const friendly = FriendlyErrorMapper.map(error);
    return (
      <div className="text-center py-16">
        <p className="text-white/70 text-sm mb-2">{friendly.title}</p>
        <p className="text-white/40 text-xs">{friendly.description}</p>
      </div>
    );
  }

  return (
    <div>
      {/* Toolbar */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          {folderStack.length > 0 && (
            <button
              onClick={onNavigateBack}
              className="flex items-center gap-1 text-sm text-white/50 hover:text-white/80 transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
              {t("cloudPicker.back")}
            </button>
          )}
        </div>
        <button
          onClick={onRefresh}
          className="p-1.5 rounded-lg hover:bg-white/[0.06] text-white/30 hover:text-white/60 transition-colors"
          title={t("cloudPicker.refresh")}
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {importError && (
        <div className="mb-4 px-4 py-2 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
          {t("cloudPicker.importFailed", { message: importError.message })}
        </div>
      )}

      {files.length === 0 ? (
        <div className="text-center py-16">
          <Cloud className="w-10 h-10 text-white/20 mx-auto mb-3" />
          <p className="text-white/40 text-sm">{t("cloudPicker.emptyFolder")}</p>
        </div>
      ) : (
        <div className="space-y-1">
          {files.map((file) => (
            <FileRow
              key={file.id}
              file={file}
              isImporting={importingFileId === file.id}
              onNavigateFolder={onNavigateFolder}
              onImport={onImport}
              actionLabel={actionLabel}
              locale={locale}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function FileRow({
  file,
  isImporting,
  onNavigateFolder,
  onImport,
  actionLabel,
  locale,
}: {
  file: CloudFile;
  isImporting: boolean;
  onNavigateFolder: (file: CloudFile) => void;
  onImport: (file: CloudFile) => void;
  actionLabel: string;
  locale: string;
}) {
  const { t } = useTranslation();
  const handleClick = () => {
    if (file.isFolder) return onNavigateFolder(file);
    onImport(file);
  };

  return (
    <button
      onClick={handleClick}
      disabled={isImporting}
      title={
        file.isFolder
          ? t("cloudPicker.openFolder")
          : t("cloudPicker.actionFile", { action: actionLabel })
      }
      className={`
        w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all
        hover:bg-white/[0.04] active:bg-white/[0.06]
        ${isImporting ? "opacity-60 pointer-events-none" : ""}
      `}
    >
      {/* Thumbnail or icon */}
      {file.thumbnailUrl && !file.isFolder ? (
        <img
          src={file.thumbnailUrl}
          alt=""
          className="w-10 h-10 rounded-lg object-cover bg-white/[0.04]"
        />
      ) : (
        <div className="w-10 h-10 rounded-lg bg-white/[0.04] flex items-center justify-center">
          {getFileIcon(file)}
        </div>
      )}

      {/* Name & meta */}
      <div className="flex-1 min-w-0">
        <p className="text-sm text-white truncate">{file.name}</p>
        <p className="text-xs text-white/30">
          {file.isFolder ? t("cloudPicker.folder") : formatFileSize(file.size)}
          {file.modifiedAt && ` • ${new Date(file.modifiedAt).toLocaleDateString(locale)}`}
        </p>
      </div>

      {/* Action */}
      {isImporting ? (
        <Loader2 className="w-4 h-4 text-white/40 animate-spin" />
      ) : file.isFolder ? (
        <FolderOpen className="w-4 h-4 text-white/20" />
      ) : (
        <Download className="w-4 h-4 text-white/20" />
      )}
    </button>
  );
}
